import { z } from 'zod';
import { request, useMutation, useQuery, useQueryClient } from '@applyboard/ui-utils';
import { VasError } from '../common/errors/VasError';
import { TestVoucherStatusType, TestVoucherTypesType } from '../pages/TestVouchers/types';
import { refetchType } from '.';

const RawTestVoucherSchema = z.object({
  id: z.string(),
  type: z.literal('voucher'),
  attributes: z.object({
    voucher_code: z.string(),
    type: z.string(),
    owner_id: z.string(),
    status: z.enum(['assigned', 'unassigned']),
    student_id: z.string().nullish(),
    student_first_name: z.string().nullish(),
    student_last_name: z.string().nullish(),
    student_email: z.string().nullish(),
    created_at: z.string(),
    assigned_at: z.string().nullish(),
    purchased_at: z.string(),
    expires_at: z.string(),
  }),
});

export type RawTestVoucher = z.infer<typeof RawTestVoucherSchema>;

const RawTestVoucherDataSchema = z.object({
  data: z.array(RawTestVoucherSchema),
});

export type RawTestVoucherData = z.infer<typeof RawTestVoucherDataSchema>;

// Utility function to calculate statistics from voucher data
export function calculateVoucherStatistics(vouchers: RawTestVoucher[]) {
  const totalVouchers = vouchers?.length || 0;
  const totalAssigned =
    vouchers?.filter(voucher => voucher.attributes.status === 'assigned')?.length || 0;
  const totalUnassigned =
    vouchers?.filter(voucher => voucher.attributes.status === 'unassigned')?.length || 0;

  return {
    total_vouchers: totalVouchers,
    total_assigned: totalAssigned,
    total_unassigned: totalUnassigned,
  };
}

const DEFAULT_PAGINATION = {
  pageNum: 1,
  size: 50,
};

export interface useGetTestVouchersProps {
  pagination?: {
    pageNum: number;
    size: number;
  };
  statusFilter?: TestVoucherStatusType;
  typeFilter?: TestVoucherTypesType;
}

export function useGetTestVouchers(
  { pagination = DEFAULT_PAGINATION, statusFilter, typeFilter }: useGetTestVouchersProps = {
    pagination: DEFAULT_PAGINATION,
  },
  {
    enabled,
    retryCount,
    retryOnMount,
  }: { enabled?: boolean; retryCount?: number; retryOnMount?: boolean } = { retryCount: 2 },
) {
  const { isLoading, isFetching, error, data, refetch } = useQuery(
    ['useGetTestVouchers', pagination, statusFilter, typeFilter, { pkg: 'ab-vas-gic' }],
    async () => {
      const response = await request<RawTestVoucherData>('/api/vas_hub/vouchers/v1', {
        schema: RawTestVoucherDataSchema,
        allowAnonymous: false,
      });
      const vouchers: RawTestVoucher[] = response?.data;

      const filteredData = vouchers
        ?.filter(
          value =>
            (!statusFilter || value.attributes.status === statusFilter) &&
            (!typeFilter || value.attributes.type === typeFilter),
        )
        .sort((a, b) => a.id.localeCompare(b.id));

      const zeroBasedPageNum = pagination.pageNum - 1;
      const startIndex = zeroBasedPageNum * pagination.size;
      const endIndex = startIndex + pagination.size;
      const paginatedData = filteredData?.slice(
        startIndex >= filteredData.length ? filteredData.length : startIndex,
        endIndex >= filteredData.length ? filteredData.length : endIndex,
      );

      // Calculate statistics from the full filtered dataset (before pagination)
      const statistics = calculateVoucherStatistics(filteredData);

      return {
        vouchers: paginatedData,
        statistics,
        meta: {
          statistics,
          total: filteredData.length,
        },
      };
    },
    {
      enabled: enabled,
      refetchOnMount: false,
      retryOnMount: retryOnMount,
      retry: retryCount,
    },
  );

  return {
    testVouchers: data?.vouchers,
    statistics: data?.statistics,
    totalCount: data?.meta.total,
    metaStatistics: data?.meta.statistics,
    testVouchersError: error,
    isLoadingTestVouchers: isLoading,
    isFetchingTestVouchers: isFetching,
    refetch: refetch as refetchType,
  };
}

/**
 * Assign voucher to student
 */

const AssignVoucherParamSchema = z.object({
  studentId: z.string(),
  voucherId: z.string(),
});

export type AssignVoucherParam = z.infer<typeof AssignVoucherParamSchema>;

const AssignVoucherResponseSchema = z.object({
  student_id: z.string(),
  voucher_id: z.string(),
  status: z.string(),
});

export type AssignVoucherResponse = z.infer<typeof AssignVoucherResponseSchema>;

export function useAssignVoucherToStudent() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(async (params: AssignVoucherParam) => {
    const payload = {
      student_id: params.studentId,
    };

    return request(`/api/vas_hub/vouchers/v1/${params.voucherId}/assign`, {
      body: payload,
      method: 'POST',
      allowAnonymous: false,
      schema: AssignVoucherResponseSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new VasError(jsonMessage, 'Assigning voucher to student failed');
      },
    });
  });

  const assignVoucherToStudent = (
    params: AssignVoucherParam,
    onSuccess?: (data: AssignVoucherResponse) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: (response: AssignVoucherResponse) => {
        queryClient.invalidateQueries([`/api/vas_hub/vouchers/v1/${params.voucherId}/assign`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    assignVoucherToStudent,
    isAssigningVoucherToStudent: isLoading,
    assinVoucherToStudentError: isError,
  };
}

/**
 * Unassign voucher
 */

const UnassignVoucherResponseSchema = z.object({
  voucher_id: z.string(),
  status: z.string(),
});

export type UnassignVoucherResponse = z.infer<typeof UnassignVoucherResponseSchema>;

export function useUnassignVoucher() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(async (voucherId: string) => {
    const payload = {
      student_id: voucherId,
    };

    return request(`/api/vas_hub/vouchers/v1/${voucherId}/unassign`, {
      body: payload,
      method: 'POST',
      allowAnonymous: false,
      schema: UnassignVoucherResponseSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new VasError(jsonMessage, 'Unassigning voucher failed');
      },
    });
  });

  const unassignVoucher = (
    voucherId: string,
    onSuccess?: (data: UnassignVoucherResponse) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(voucherId, {
      onSuccess: (response: UnassignVoucherResponse) => {
        queryClient.invalidateQueries([`/api/vas_hub/vouchers/v1/${voucherId}/assign`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    unassignVoucher,
    isUnassigningVoucher: isLoading,
    unassignVoucherError: isError,
  };
}

/**
 * Send Voucher by Email
 */
const SendVoucherByEmailResponseSchema = z.object({
  voucher_id: z.string(),
  message: z.string(),
});

export type SendVoucherByEmailResponse = z.infer<typeof SendVoucherByEmailResponseSchema>;

export function useSendVoucherByEmail() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(async (voucherId: string) => {
    return request(`/api/vas_hub/vouchers/v1/${voucherId}/send_email`, {
      method: 'POST',
      allowAnonymous: false,
      schema: SendVoucherByEmailResponseSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new VasError(jsonMessage, 'Sending voucher by email failed');
      },
    });
  });

  const sendVoucherByEmail = (
    voucherId: string,
    onSuccess?: (data: SendVoucherByEmailResponse) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(voucherId, {
      onSuccess: (response: SendVoucherByEmailResponse) => {
        queryClient.invalidateQueries([`/api/vas_hub/vouchers/v1/${voucherId}/send_email`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    sendVoucherByEmail,
    isSendingVoucherByEmail: isLoading,
    sendVoucherByEmailError: isError,
  };
}

/**
 * Send Voucher by Whatsapp
 */

const SendVoucherByWhatsappResponseSchema = z.object({
  voucher_id: z.string(),
  message: z.string(),
});

export type SendVoucherByWhatsappResponse = z.infer<typeof SendVoucherByWhatsappResponseSchema>;

export function useSendByWhatsapp() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async ({ voucherId, whatsappNumber }: { voucherId: string; whatsappNumber: string }) => {
      const payload = { whatsapp_number: whatsappNumber };

      return request(`/api/vas_hub/vouchers/v1/${voucherId}/send_whatsapp`, {
        body: payload,
        method: 'POST',
        allowAnonymous: false,
        schema: SendVoucherByEmailResponseSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new VasError(jsonMessage, 'Sending voucher by Whatsapp failed');
        },
      });
    },
  );

  const sendVoucherByWhatsapp = (
    {
      voucherId,
      whatsappNumber,
    }: {
      voucherId: string;
      whatsappNumber: string;
    },
    onSuccess?: (data: SendVoucherByEmailResponse) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(
      { voucherId, whatsappNumber },
      {
        onSuccess: (response: SendVoucherByEmailResponse) => {
          queryClient.invalidateQueries([`/api/vas_hub/vouchers/v1/${voucherId}/send_whatsapp`]);
          onSuccess?.(response);
        },
        onError: (error: unknown) => onError?.(error),
      },
    );
  };
  return {
    sendVoucherByWhatsapp,
    isSendingVoucherByWhatsapp: isLoading,
    sendVoucherByWhatsappError: isError,
  };
}
